import { queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiGet, withParams } from './client';
import { clampPollMs } from './pollInterval';
import { readStart, startKey, writeStart } from './startCache';
import type { HighlightsScreen, NewsArticle, NewsFeedPage, NewsHub } from './leagueTypes';
import type { ServerTabBar } from './matchTypes';

// Вкладки хаба последнего ответа (HQ-1907, Figma 772:30737): без сети и при ошибке полоса раздела остаётся над
// ErrorView. Источник — ответ этого запуска (память), иначе кэш холодного старта вида news (только tab_bar, не старше
// суток, ключ с адресом хаба). Нет ни того, ни другого — набор вкладок неизвестен, полосы нет.
const remembered = new Map<string, ServerTabBar>();

export function useNewsHub(path: string) {
  return useQuery({
    queryKey: ['news-hub', path],
    queryFn: async ({ signal }) => {
      const hub = await apiGet<NewsHub>(path, signal);
      if (hub.tab_bar?.items?.length) {
        remembered.set(path, hub.tab_bar);
        writeStart(startKey('news', path), hub.tab_bar);
      }
      return hub;
    },
    staleTime: 5 * 60_000,
  });
}

/** Известные вкладки хаба без его ответа: память этого запуска, иначе кэш старта; undefined — неизвестны. */
export function knownNewsTabBar(path: string): ServerTabBar | undefined {
  const bar = remembered.get(path) ?? readStart<ServerTabBar>(startKey('news', path))?.data;
  return Array.isArray(bar?.items) && bar.items.length ? bar : undefined; // битая запись кэша — как нет записи
}

/** Лента: адрес — серверный feed_path / more_path как есть; страница — ?cursor=<next_cursor> (число). */
export function useNewsFeed(path: string) {
  return useInfiniteQuery({
    queryKey: ['news-feed', path],
    queryFn: ({ pageParam, signal }) => apiGet<NewsFeedPage>(withParams(path, { cursor: pageParam ?? undefined }), signal),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.next_cursor,
    staleTime: 5 * 60_000,
  });
}

export const useArticle = (id: string) => useQuery({
  queryKey: ['article', id], queryFn: ({ signal }) => apiGet<NewsArticle>(`/api/v5/mobile/screens/cricket/news/${id}/`, signal),
  staleTime: 30 * 60_000,
});

/** path — url вкладки highlights из config как есть; без вкладки (экран /videos) — тот же известный адрес.
 *  teams — наш набор избранных команд (как у полки и хаба): присланный ключ перекрывает аккаунт, пустой — «нет». */
/** staleTime 120 с: возврат в Telegram и повторное открытие не перезапрашивают полку; опрос — по poll (HQ-1646). */
export const highlightsOptions = (league: string | null, path = '/api/v5/mobile/screens/cricket/highlights/', teams?: string) => queryOptions({
  queryKey: ['highlights', path, league, teams],
  queryFn: ({ signal }) => apiGet<HighlightsScreen>(withParams(path, { league: league ?? undefined, teams }), signal),
  refetchInterval: (q) => clampPollMs(q.state.data?.poll?.interval_seconds),
  refetchIntervalInBackground: false,
  staleTime: 2 * 60_000,
});

export const useHighlights = (league: string | null, path?: string, teams?: string) =>
  useQuery(highlightsOptions(league, path, teams));
